import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import useGetDeviceById from '../../API/hooks/devices/useGetDeviceById';
import useGetDeviceLocations from '../../API/hooks/maps/useGetDeviceLocations';
import useAssignVehicleToDevice from '../../API/hooks/vehicles/useAssignVehicleToDevice';
import useGetVehicleById from '../../API/hooks/vehicles/useGetVehicleById';
import DeviceSmallThumbnail from '../../assets/images/device-small.png';
import useVehicleThumbnail from '../../theme/useVehicleThumbnail';
import Icon from '../../components/common/Icon/Icon';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import MapWidget from '../../components/common/Map/Map';
import DetailsPageSkeleton from '../../components/common/Skeleton/DetailsPageSkeleton';
import { Button } from '../../components/ui/Button/Button';
import HeaderWrapper from '../../components/ui/DetailsPageHeaderWrapper/HeaderWrapper';
import Modal from '../../components/common/Modal/Modal';
import Tags from '../../components/common/Tags';
import HiddenDetails, { HiddenDetailTagItem } from '../../components/common/Tags/HiddenDetails/HiddenDetails';
import DetailsWidget from '../../components/common/DetailsWidget/DetailsWidget';
import TimeSelection from '../../components/common/Toolbar/TimeSelection';
import { ThemeType } from '../../theme/themeType';
import { renderSubtitle } from '../Devices/helpers';
import DeviceSettings from './Modals/DeviceSettings/DeviceSettings';
import S from './DeviceDetailsStyles';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetRole from '../../API/hooks/useGetRole';
import CreateNote from '../../components/Notes/CreateNote/CreateNote';
import useGetAlertsAggregation from '../../API/hooks/alerts/useGetAlertsAggregation';
import { getFilterFields, getSimpleEqQuery, queryFilters } from '../../utils/queryFilters';
import { generateEventsLabel } from '../../utils/commonUtils';
import DetailsPanel from '../../components/DetailsPanel/DetailsPanel';
import useGetTrips from '../../API/hooks/trips/useGetTrips';
import useGetAlerts from '../../API/hooks/alerts/useGetAlerts';
import { getEndOfTheDay, getFormattedDateAndTime, getStartOfMonth } from '../../utils/dateUtils';
import { getLocalStorageKey } from '../../utils/storageUtils';
import useGetDriverById from '../../API/hooks/drivers/useGetDriverById';
import { deviceDetailsPageFilters } from '../../constants/FilterDefault';
import useLocalStorage from '../../API/hooks/useLocalStorage';
import useGetDeviceConfiguration from '../../API/hooks/devices/useGetDeviceConfiguration';
import { getDeviceConfigStatusDisplay } from '../../utils/deviceConfigStatusDisplay';
import useDetailsListNavigation from '../../components/common/DetailsListNavigation/DetailsListNavigation';
import { entityMatchingId } from '../../utils/entityMatching';

const cameraBlockageStatus = [
  'No blockage',
  'Partial blockage',
  'Total blockage',
  'No data',
];

function DeviceDetailsPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const fleetId = useGetFleetId();
  const {
    isFleetViewer, isAdmin, isSupport, isFleetManager,
  } = useGetRole();
  const navigate = useNavigate();
  const { id: deviceId } = useParams();
  const listNavigation = useDetailsListNavigation({
    entity: 'devices',
    currentId: deviceId,
  });
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab');
  const isWideMapTab = !activeTab || activeTab === 'calendar' || activeTab === 'connectivity' || activeTab === 'alert';
  const { data: device, isLoading, error } = useGetDeviceById(deviceId as string, fleetId);
  const { data: vehicle } = useGetVehicleById(device?.vehicleId as string, fleetId);
  const { data: driver } = useGetDriverById(vehicle?.driverId as string, fleetId, !!vehicle?.driverId);
  const deviceVehicle = entityMatchingId(vehicle, vehicle?._id, device?.vehicleId);
  const deviceDriver = entityMatchingId(driver, driver?._id, deviceVehicle?.driverId);
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('device-page-details'), deviceDetailsPageFilters);
  const activeFilters = filters || deviceDetailsPageFilters;
  const tripTabFilters = { sortBy: ['startAt_DESC'], ...activeFilters };
  const tripsTotalQuery = queryFilters(
    tripTabFilters,
    1,
    getSimpleEqQuery('deviceId', device?.deviceId, 9),
    'startAt',
    1,
  );
  const { data: tripsTotalData } = useGetTrips(fleetId, tripsTotalQuery, !!device?.deviceId);
  const tripsTotal = tripsTotalData?.total ?? 0;
  const { data: deviceEvents } = useGetAlertsAggregation(fleetId, `?limit=1000&skip=0${getSimpleEqQuery('deviceId', device?.deviceId, 0)}${getFilterFields(activeFilters, ['period'], 'createdAt', 1)}`);
  const { data: trip } = useGetTrips(fleetId, `?limit=1&skip=0&sortField=createdAt&sortOrder=DESC${getSimpleEqQuery('deviceId', device?.deviceId, 1)}`);
  const tripData = trip?.results?.[0];
  const { data: alerts, isSuccess: alertsLoaded } = useGetAlerts(
    fleetId,
    queryFilters(activeFilters, undefined, getSimpleEqQuery('deviceId', device?.deviceId as string, 20), 'eventDate'),
    !!device?.deviceId,
  );
  const { data: deviceConfig, isLoading: isConfigLoading, isError: isConfigError } = useGetDeviceConfiguration(device?.deviceId as string, fleetId ?? '');
  const [deviceSettingModalOpen, toggleChangeSettingsModal] = useState(false);
  const [addNoteModalOpen, toggleAddNoteModalOpen] = useState(false);

  const handleBackToList = (): void => navigate('/devices/');

  const handleGoBack = (): void => {
    if (listNavigation.backToList) {
      listNavigation.backToList();
      return;
    }
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      handleBackToList();
    }
  };

  const handleAddNote = (): void => {
    toggleAddNoteModalOpen(true);
  };

  // TODO remove after DEMO
  const demoDevice = {
    link: 'https://track.roadcast.co.in/auth/login?basic=VmFycm9jOkFkbWluQDEyMw==',
    name: 'TK9876R68',
    data: 'Roadcast device',
  };

  useEffect(() => {
    if (error) {
      navigate('/devices/');
    }
  }, [error, navigate]);

  const {
    icon: configIcon,
    color: configColor,
    text: configText,
    textColor: configTextColor,
  } = getDeviceConfigStatusDisplay(deviceConfig?.status, isConfigLoading, isConfigError);

  const hiddenDeviceTagItems: HiddenDetailTagItem[] = [{
    key: 'device-details-more-tags',
    node: (
      <>
        <Tags.Label label={device?.deviceId || 'N/A'} />
        <Tags.Label label={`HW: ${device?.hwModel || 'N/A'}`} />
        {(isAdmin || isSupport || isFleetManager) && (
          <Tags.Label label={`FW: ${device?.fwVersion || 'N/A'}`} />
        )}
        {deviceEvents?.count != null && (
          <Tags.LabeledIcon
            flexible
            label={generateEventsLabel(deviceEvents.count)}
            icon={IconKeys.ALERT_CIRCLE}
          />
        )}
        {tripsTotal != null && (
          <Tags.Label
            label={`${tripsTotal} trips in total`}
          />
        )}
        {!!device?.vehicleAssignedDate && (
          <Tags.Label
            label={`Vehicle assigned: ${getFormattedDateAndTime(device.vehicleAssignedDate)}`}
          />
        )}
        {device?.interiorCameraBlocked !== null && device?.interiorCameraBlocked !== undefined && (
          <Tags.Label
            label={`Interior camera: ${cameraBlockageStatus[+device.interiorCameraBlocked]}`}
          />
        )}
        {device?.exteriorCameraBlocked !== null && device?.exteriorCameraBlocked !== undefined && (
          <Tags.Label
            label={`Exterior camera: ${cameraBlockageStatus[+device.exteriorCameraBlocked]}`}
          />
        )}
      </>
    ),
  }];

  return (
    <>
      {isLoading && (<DetailsPageSkeleton />)}

      {!!device && (
        <S.Wrapper>
          <HeaderWrapper>
            <div className="details-row">
              <div className="overview-container">
                <div className="preview-container">
                  <Button height="44px" padding="10px 12px" onClick={handleGoBack}>
                    <Icon iconKey={IconKeys.ARROW_LEFT} />
                  </Button>
                  <Tags.ItemOverview
                    avatar={DeviceSmallThumbnail}
                    avatarAlt="device"
                    title={device.hwModel}
                    subtitle={renderSubtitle(device.activeAt, device.isOnline)}
                    isIcon
                  />
                  {listNavigation.controls}
                </div>
                {!!device?.vehicle && (
                  <Tags.WithUnassign
                    link={`/vehicles/${device.vehicle._id}`}
                    imageSrc={vehicleThumbnail}
                    title={device.vehicle.licensePlateNumber}
                    description={`${device.vehicle.manufacturer ? (`${device.vehicle.manufacturer} `) : ''}${device.vehicle.model ? (`${device.vehicle.model} `) : ''}`}
                    apiCall={useAssignVehicleToDevice}
                    mutationConfig={{ deviceId: deviceId as string, fleetId, vehicleId: null }}
                  />
                )}
              </div>
              <div className="actions-wrapper">
                <div className="border-line" />
                {/* <Button
                  height="44px"
                  padding="10px 12px"
                  title="Add note"
                  onClick={handleAddNote}
                >
                  <Icon iconKey={IconKeys.ADD_NOTE} />
                </Button> */}
                {/* TODO remove after DEMO */}
                {demoDevice && device.deviceId.includes('demo') && (
                  <Tags.DeviceInfo
                    link={demoDevice.link}
                    title={demoDevice.name}
                    description={demoDevice.data}
                  />
                )}
                {!isFleetViewer && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Settings"
                    onClick={() => toggleChangeSettingsModal(true)}
                  >
                    <Icon iconKey={IconKeys.SETTINGS} />
                  </Button>
                )}
              </div>
            </div>
            <div className="sub-header">
              <div className="tags-row">
                <Tags.Status
                  backgroundColor={device.isOnline ? theme.colors.green : theme.colors.red}
                  label={device.isOnline ? 'Online' : 'Offline'}
                />
                <Tags.LabeledIcon
                  label={configText}
                  icon={configIcon}
                  backgroundColor={configColor}
                  textColor={configTextColor}
                  flexible
                />
                <HiddenDetails items={hiddenDeviceTagItems} label="More details..." />
              </div>
              <TimeSelection allTimeOption filters={filters} setFilters={setFilters} />
            </div>
          </HeaderWrapper>
          <div className="widgets-container">
            <DetailsWidget
              key={deviceId}
              fleetId={fleetId}
              entity="device"
              entityId={device?.deviceId as string}
              filters={filters}
              showTabs={{
                calendar: true,
                alert: true,
                trip: true,
                video: true,
                note: true,
                connectivity: isAdmin || isSupport || isFleetManager,
              }}
            />
            {/* <MapWidget
              fleetId={fleetId}
              getLocationsQuery={useGetDeviceLocations}
              entityId={tripData?.deviceId as string}
              entityType="device"
              startDate={tripData?.startAt}
              endDate={tripData?.endAt || getEndOfTheDay().toISOString()}
              alerts={alerts?.results}
              alertsLoaded={alertsLoaded}
              isTrip
              hasTripVehicleMarker
            /> */}
            <MapWidget
              fleetId={fleetId}
              getLocationsQuery={useGetDeviceLocations}
              entityId={device?.deviceId as string}
              entityType="device"
              hasActiveTripForSpeed={!!tripData && tripData.endAt == null}
              showDetailsButton={false}
              routeHistoryFilters={activeFilters}
              mapWrapperWidth={isWideMapTab ? '50%' : '403px'}
              alerts={alerts?.results}
              alertsLoaded={alertsLoaded}
            />
          </div>
          <DetailsPanel driver={deviceDriver} vehicle={deviceVehicle} device={device} />
          {deviceSettingModalOpen && (
            <Modal
              title="Device Settings"
              state={deviceSettingModalOpen}
              toggleState={toggleChangeSettingsModal}
              settings={{ maxWidth: '600px', minHeight: '550px', display: 'block' }}
            >
              <DeviceSettings
                fleetId={fleetId}
                device={device}
                toggleModal={toggleChangeSettingsModal}
              />
            </Modal>
          )}
          {addNoteModalOpen && deviceId && device?.deviceId && (
            <Modal
              title="Add note for device"
              state={addNoteModalOpen}
              toggleState={toggleAddNoteModalOpen}
            >
              <CreateNote fleetId={fleetId} entityName="devices" entityId={device.deviceId} toggleModal={toggleAddNoteModalOpen} />
            </Modal>
          )}
        </S.Wrapper>
      )}
    </>
  );
}

export default DeviceDetailsPage;
